import { Stack } from '@mui/material';
import { isCPFOrCNPJ } from 'brazilian-values';
import { FormikProvider, useFormik } from 'formik';
import { useMemo, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import { getCardProcessingProviderByName } from '@woovi/card-processing-provider';
import { useMutationCallbacks } from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton } from '@woovi/ui';

import type { PaymentActiveWaitingPaymentMethodCreditCard_charge$key } from './__generated__/PaymentActiveWaitingPaymentMethodCreditCard_charge.graphql.ts';
import { PaymentActiveWaitingPaymentMethodCreditCardFormFields } from './PaymentActiveWaitingPaymentMethodCreditCardFormFields.tsx';
import PaymentMethodCreditCardAddBillingAddressModal from './PaymentMethodCreditCardAddBillingAddressModal.tsx';
import type { BillingAddressType } from './PaymentMethodCreditCardAddBillingAddressModal.tsx';
import PaymentMethodCreditCardValue from './PaymentMethodCreditCardValue.tsx';
import PaymentLinkAdditionalInfo from '../../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../../paymentLink/PaymentLinkComment.tsx';
import PaymentMethodPaymentDeadline from '../../paymentMethod/PaymentMethodPaymentDeadline.tsx';
import PaymentMethodTransactionDetails from '../../paymentMethod/PaymentMethodTransactionDetails.tsx';
import type { ChargeAddPaymentMethodMutation } from '../mutations/__generated__/ChargeAddPaymentMethodMutation.graphql.ts';
import { ChargeAddPaymentMethod } from '../mutations/ChargeAddPaymentMethodMutation.tsx';

type PaymentActiveWaitingPaymentMethodCreditCardValues = {
  holderName: string;
  holderTaxID: string;
  number: string;
  expireDate: string;
  cvv: string;
  billingAddress: string;
};

type PaymentActiveWaitingPaymentMethodCreditCardProps = {
  charge: PaymentActiveWaitingPaymentMethodCreditCard_charge$key;
};
const PaymentActiveWaitingPaymentMethodCreditCard = (
  props: PaymentActiveWaitingPaymentMethodCreditCardProps,
): ReactNode => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [address, setAddress] = useState();
  const addressFieldRef = useRef(null);

  const charge =
    useFragment<PaymentActiveWaitingPaymentMethodCreditCard_charge$key>(
      graphql`
        fragment PaymentActiveWaitingPaymentMethodCreditCard_charge on Charge {
          id
          status
          identifier
          availablePaymentMethods
          company {
            nameFriendly
            taxID {
              taxID
            }
          }
          customer {
            name
            taxID {
              taxID
            }
          }
          valueWithDiscount
          ...PaymentLinkComment_charge
          ...PaymentLinkAdditionalInfo_charge
          ...PaymentActiveCreditCardValue_charge
          ...PaymentMethodPaymentDeadline_charge
          ...PaymentMethodTransactionDetails_charge
          ...PaymentMethodCreditCardValue_charge
          cardProcessingProvider
        }
      `,
      props.charge,
    );

  const [chargeAddPaymentMethod, isPendingChargeAddPaymentMethod] =
    useMutationCallbacks<ChargeAddPaymentMethodMutation>({
      name: 'ChargeAddPaymentMethod',
      mutation: ChargeAddPaymentMethod,
    });

  const handleChargeAddPaymentMethod = async (
    values: PaymentActiveWaitingPaymentMethodCreditCardValues,
  ): Promise<void> => {
    if (loading) {
      return;
    }

    setLoading(true);

    const expMonth = values.expireDate.split('/')[0];
    const expYear = values.expireDate.split('/')[1];

    const cardTokenize = {
      holderName: values.holderName,
      holderTaxID: values.holderTaxID,
      number: values.number,
      expMonth: parseInt(expMonth, 10),
      expYear: parseInt(expYear, 10),
      cvv: values.cvv,
    };

    const getCardSanitized = async () => {
      const provider = getCardProcessingProviderByName(
        charge.cardProcessingProvider,
      );

      if (!provider) {
        return cardTokenize;
      }

      const creditCardTokenizeResult = await provider.creditCardTokenize({
        cardTokenizePayload: cardTokenize,
        t,
      });

      if (!creditCardTokenizeResult.success) {
        return cardTokenize;
      }

      return {
        holderTaxID: cardTokenize.holderTaxID,
        expMonth: cardTokenize.expMonth,
        expYear: cardTokenize.expYear,
        token: creditCardTokenizeResult.token,
      };
    };

    const card = await getCardSanitized();

    chargeAddPaymentMethod({
      variables: {
        input: {
          chargeId: charge.id,
          paymentMethodType: PAYMENT_METHOD_TYPE.CREDIT_CARD,
          paymentMethodCreditCardConfig: {
            value: charge.valueWithDiscount,
            billingAddress: address,
            installmentNumber: 1,
            card,
          },
        },
      },
    });

    setLoading(false);
  };

  const initialValues = useMemo(
    () => ({
      holderName: '',
      holderTaxID: charge?.customer?.taxID?.taxID,
      number: '',
      expireDate: '',
      cvv: '',
      billingAddress: '',
    }),
    [],
  );

  const validationSchema = yup.object().shape({
    holderName: yup.string().required(t('Name is required')),
    holderTaxID: yup
      .string()
      .test('isCPF', t('Customer TaxID is invalid'), (value) => {
        if (!value) {
          return true;
        }

        return isCPFOrCNPJ(value);
      })
      .required(t('TaxID is required')),
    number: yup.string().required(t('Card expiration is mandatory')),
    expireDate: yup.string().required(t('Card expiration is mandatory')),
    cvv: yup.string().required(t('Card code is required')),
    billingAddress: yup.string().required(t('Billing address is required')),
  });

  const setBillingAddress = (values: BillingAddressType): void => {
    const { street, number, neighborhood, city, state } = values.address;
    const formattedAddress = `${street}, ${number}, ${neighborhood}, ${city}, ${state}`;

    formikbag.setFieldValue('billingAddress', formattedAddress);
    formikbag.setFieldTouched('billingAddress', true, true);

    const billingAddress = {
      ...values.address,
      country: 'BR',
    };

    setAddress(billingAddress);
    toggleModal();
  };

  const formikbag =
    useFormik<PaymentActiveWaitingPaymentMethodCreditCardValues>({
      validateOnBlur: true,
      validateOnMount: true,
      enableReinitialize: true,
      initialValues,
      validationSchema,
      onSubmit: handleChargeAddPaymentMethod,
    });

  const { isValid, handleSubmit } = formikbag;

  const toggleModal = () => {
    if (modalOpen) {
      setModalOpen(false);

      return;
    }

    setModalOpen(true);
  };

  return (
    <>
      <FormikProvider value={formikbag}>
        <Stack sx={{ gap: 2 }}>
          <PaymentMethodCreditCardValue charge={charge} />
          <PaymentLinkComment charge={charge} />
          <Stack
            sx={{
              gap: 2,
              '@media (min-width: 600px)': {
                px: 5,
              },
            }}
          >
            <PaymentActiveWaitingPaymentMethodCreditCardFormFields
              toggleModal={toggleModal}
              addressFieldRef={addressFieldRef}
            />
            <ActionButton
              disabled={!isValid || isPendingChargeAddPaymentMethod || loading}
              onClick={handleSubmit}
              loading={isPendingChargeAddPaymentMethod || loading}
            >
              {t('Pay')}
            </ActionButton>
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <PaymentMethodPaymentDeadline charge={charge} />
            <PaymentMethodTransactionDetails charge={charge} />
            <PaymentLinkAdditionalInfo charge={charge} />
          </Stack>
        </Stack>
      </FormikProvider>
      <PaymentMethodCreditCardAddBillingAddressModal
        open={modalOpen}
        onClose={toggleModal}
        setBillingAddress={setBillingAddress}
      />
    </>
  );
};

export default PaymentActiveWaitingPaymentMethodCreditCard;
